import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus, Loader2, Calendar as CalendarIcon, ChevronLeft, Trash2, ClipboardList, CircleDollarSign, CheckCircle2, Pencil, ImageIcon, MapPin } from "lucide-react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import LocationPickerDialog, { LocationViewerDialog } from "../components/LocationPickerDialog";
import { api, formatApiError } from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useAuth } from "../context/AuthContext";
import { getCurrency } from "../lib/currency";

const MONTHS_AR = [
    "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
    "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

/** Arabic label for a stored booking_type value. */
const BOOKING_TYPE_LABEL_AR = {
    full: "كامل",
    partial: "جزئي",
    full_no_delivery: "كامل بدون توصيل",
    full_with_trainee_car: "كامل مع سيارة المتدرب",
    trainee_car_no_delivery: "حجز بسيارة المتدربة بدون توصيل",
};

/** Arabic weekday names, indexed by Date.getDay() (0=Sunday). */
const WEEKDAYS_AR = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

/** Derive the Arabic weekday name for a "YYYY-MM-DD" string. */
function weekdayAr(dateStr) {
    if (!dateStr) return "";
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return "";
    return WEEKDAYS_AR[d.getDay()];
}

/** 24h "HH:MM" → { hour12: "1".."12", minute: "00".."59", period: "am"|"pm" } */
function parse24h(hhmm) {
    if (!hhmm || typeof hhmm !== "string") return { hour12: "8", minute: "00", period: "am" };
    const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return { hour12: "8", minute: "00", period: "am" };
    let h = parseInt(m[1], 10);
    const min = m[2];
    const period = h >= 12 ? "pm" : "am";
    h = h % 12;
    if (h === 0) h = 12;
    return { hour12: String(h), minute: min, period };
}

/** { hour12, minute, period } → 24h "HH:MM" */
function build24h({ hour12, minute, period }) {
    let h = parseInt(hour12, 10);
    if (Number.isNaN(h)) h = 8;
    if (period === "am") {
        if (h === 12) h = 0;
    } else if (h < 12) {
        h = h + 12;
    }
    const min = /^\d{2}$/.test(minute) ? minute : "00";
    return `${String(h).padStart(2, "0")}:${min}`;
}

/** Arabic 12h display for a stored 24h "HH:MM" (e.g. "14:30" → "2:30 مساءً"). */
function fmtTimeAr(hhmm) {
    if (!hhmm) return "";
    const { hour12, minute, period } = parse24h(hhmm);
    return `${hour12}:${minute} ${period === "am" ? "صباحاً" : "مساءً"}`;
}

/**
 * Car Learn — booking ledger.
 * Two modes:
 *   - mode="home" (default, `/car-bookings`): the *customer ledger* itself.
 *     Add a booking + see the full list of saved customers/bookings.
 *   - mode="records" (`/car-bookings/records`): read-only view grouped by
 *     Months / Years. A month tile appears ONLY when a booking exists in
 *     that month — nothing is pre-created.
 */
export default function CarBookings({ mode = "home" }) {
    if (mode === "records") return <CarBookingsRecords />;
    return <CarBookingsHome />;
}

// ── HOME (Customer Ledger) ───────────────────────────────────────────
function CarBookingsHome() {
    const navigate = useNavigate();
    const { company } = useAuth();
    const cur = getCurrency(company?.country);
    const [items, setItems] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [editTarget, setEditTarget] = useState(null);  // booking obj or null
    const [payTarget, setPayTarget] = useState(null);  // booking obj or null
    const [locView, setLocView] = useState(null);  // booking obj or null — opens the read-only map
    const [loading, setLoading] = useState(true);
    // Editable cash-on-hand balance. Kept as a string in local state so the
    // user can freely edit (including empty string) without React swallowing
    // partial input. Persisted to the backend on debounce + blur.
    const [balanceStr, setBalanceStr] = useState("");
    const [balanceLoaded, setBalanceLoaded] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const r = await api.get("/car-bookings");
            setItems(r.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { loadAll(); }, [loadAll]);
    useAutoRefresh(loadAll, { intervalMs: 10000 });

    const totals = useMemo(() => items.reduce((a, b) => ({
        amount: a.amount + (b.booking_amount || 0),
        paid:   a.paid   + (b.paid_amount    || 0),
        remain: a.remain + (b.remaining_amount || 0),
        count:  a.count  + 1,
    }), { amount: 0, paid: 0, remain: 0, count: 0 }), [items]);

    // Load the stored balance once on mount.
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/car-bookings/balance");
                setBalanceStr(String(r.data?.balance ?? 0));
            } catch { /* keep default */ }
            finally { setBalanceLoaded(true); }
        })();
    }, []);

    // Debounced persistence: whenever the user edits the balance, wait 500 ms
    // and push the new value to the backend. Local computation of withdrawals
    // is instant regardless.
    useEffect(() => {
        if (!balanceLoaded) return;
        const val = parseFloat(balanceStr);
        if (!Number.isFinite(val) || val < 0) return;
        const t = setTimeout(() => {
            api.post("/car-bookings/balance", { balance: val }).catch(() => {});
        }, 500);
        return () => clearTimeout(t);
    }, [balanceStr, balanceLoaded]);

    const balanceNum = Number.isFinite(parseFloat(balanceStr)) ? parseFloat(balanceStr) : 0;
    // Per requirement: withdrawals stay at zero until the user actually types a
    // balance. Formula is `paid − balance` (only actual received cash counts,
    // unpaid booking amounts don't).
    const withdrawals = balanceNum > 0 ? Math.max(0, (totals.paid || 0) - balanceNum) : 0;

    const onBalanceChange = (e) => {
        const raw = e.target.value ?? "";
        let cleaned = raw.replace(/[^\d.]/g, "");
        const dot = cleaned.indexOf(".");
        if (dot !== -1) {
            cleaned = cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, "");
        }
        setBalanceStr(cleaned);
    };

    const onDelete = async (id) => {
        if (!window.confirm("هل تريد حذف هذا الحجز؟")) return;
        try {
            await api.delete(`/car-bookings/${id}`);
            toast.success("تم الحذف");
            loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-5xl">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-4">
                        <BrandLogo src="/img/car-learn-brand.jpeg" alt="Car Learn" />
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">Car Learn</p>
                            <h1 className="font-heading text-2xl font-bold sm:text-3xl">دفتر العملاء</h1>
                            <p className="mt-1 text-xs text-neutral-500 sm:text-sm">أضف عميلاً جديداً واعرض جميع الحجوزات المحفوظة</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate("/car-bookings/records")}
                            className="h-10 gap-2"
                            data-testid="cb-open-records-btn"
                        >
                            <ClipboardList className="h-4 w-4" /> السجلات
                        </Button>
                        <Button
                            onClick={() => setShowAdd(true)}
                            className="h-10 gap-2 bg-amber-700 hover:bg-amber-800"
                            data-testid="cb-add-btn"
                        >
                            <Plus className="h-4 w-4" /> عميل جديد
                        </Button>
                    </div>
                </div>

                {/* Summary strip */}
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="cb-home-summary">
                    <StatCard label="عدد العملاء" value={totals.count} />
                    <StatCard label="إجمالي المبالغ" value={`${totals.amount} ${cur}`} tone="brand" />
                    <StatCard label="المدفوع" value={`${totals.paid} ${cur}`} tone="green" />
                    <StatCard label="المتبقي" value={`${totals.remain} ${cur}`} tone="red" />
                    <BalanceInputCard value={balanceStr} onChange={onBalanceChange} />
                    <StatCard label="السحبيات" value={`${withdrawals} ${cur}`} tone="violet" testid="cb-withdrawals" />
                </div>

                {loading ? (
                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500">
                        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500" data-testid="cb-empty">
                        لا يوجد عملاء بعد — أضف أول حجز من زر «عميل جديد»
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
                        <table className="w-full text-right text-sm" data-testid="cb-table">
                            <thead className="bg-neutral-50 text-[11px] font-bold uppercase text-neutral-500">
                                <tr>
                                    <th className="px-3 py-2">التاريخ</th>
                                    <th className="px-3 py-2">الوقت</th>
                                    <th className="px-3 py-2">اسم العميل</th>
                                    <th className="px-3 py-2">الهاتف</th>
                                    <th className="px-3 py-2">النوع</th>
                                    <th className="px-3 py-2">ساعات</th>
                                    <th className="px-3 py-2">ساعات التدريب</th>
                                    <th className="px-3 py-2">المبلغ</th>
                                    <th className="px-3 py-2">مدفوع</th>
                                    <th className="px-3 py-2">متبقي</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => {
                                    const done = (it.remaining_amount || 0) <= 0;
                                    return (
                                    <tr key={it.id} className="border-t border-neutral-100 hover:bg-amber-50/40" data-testid={`cb-row-${it.id}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-mono text-xs">{it.date}</div>
                                            {it.date ? (
                                                <div className="mt-0.5 text-[10px] font-bold text-neutral-500" data-testid={`cb-weekday-${it.id}`}>
                                                    {weekdayAr(it.date)}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-neutral-700" data-testid={`cb-time-${it.id}`}>
                                            {it.booking_time ? fmtTimeAr(it.booking_time) : "—"}
                                        </td>
                                        <td className="px-3 py-2 font-semibold">
                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                <span>{it.customer_name}</span>
                                                {it.location_text || (it.location_lat != null && it.location_lng != null) ? (
                                                    it.location_lat != null && it.location_lng != null ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setLocView(it)}
                                                            className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-200 transition hover:bg-amber-100"
                                                            data-testid={`cb-location-chip-${it.id}`}
                                                            title={it.location_text || "فتح الخريطة"}
                                                        >
                                                            <MapPin className="h-3 w-3 shrink-0" />
                                                            <span className="truncate">
                                                                {it.location_text || "موقع محفوظ"}
                                                            </span>
                                                        </button>
                                                    ) : (
                                                        <span
                                                            className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600 ring-1 ring-neutral-200"
                                                            data-testid={`cb-location-text-${it.id}`}
                                                            title={it.location_text}
                                                        >
                                                            <MapPin className="h-3 w-3 shrink-0" />
                                                            <span className="truncate">{it.location_text}</span>
                                                        </span>
                                                    )
                                                ) : null}
                                            </div>
                                            {done ? (
                                                <span
                                                    className="mr-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 ring-1 ring-emerald-200"
                                                    data-testid={`cb-paid-badge-${it.id}`}
                                                >
                                                    <CheckCircle2 className="h-3 w-3" /> مكتمل السداد
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setPayTarget(it)}
                                                    className="mr-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800 ring-1 ring-rose-200 transition hover:bg-rose-200"
                                                    data-testid={`cb-pay-${it.id}`}
                                                    aria-label="إكمال المبلغ"
                                                >
                                                    <CircleDollarSign className="h-3 w-3" /> إكمال المبلغ
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs">{it.phone}</td>
                                        <td className="px-3 py-2">
                                            {BOOKING_TYPE_LABEL_AR[it.booking_type] || it.booking_type}
                                        </td>
                                        <td className="px-3 py-2 font-numbers">{it.hours}</td>
                                        <td className="px-3 py-2">
                                            <TrainingHoursCell booking={it} onUpdated={loadAll} />
                                        </td>
                                        <td className="px-3 py-2 font-numbers font-bold">{it.booking_amount}</td>
                                        <td className="px-3 py-2 font-numbers text-emerald-700">{it.paid_amount}</td>
                                        <td className={`px-3 py-2 font-numbers ${done ? "text-emerald-700" : "text-rose-700"}`}>{it.remaining_amount}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditTarget(it)}
                                                    className="rounded-lg p-1.5 text-neutral-400 hover:bg-sky-50 hover:text-sky-700"
                                                    data-testid={`cb-edit-${it.id}`}
                                                    aria-label="تعديل"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => onDelete(it.id)}
                                                    className="rounded-lg p-1.5 text-neutral-400 hover:bg-rose-50 hover:text-rose-600"
                                                    data-testid={`cb-del-${it.id}`}
                                                    aria-label="حذف"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {showAdd ? (
                    <AddBookingModal
                        onClose={() => setShowAdd(false)}
                        onSaved={() => { setShowAdd(false); loadAll(); }}
                    />
                ) : null}
                {editTarget ? (
                    <AddBookingModal
                        booking={editTarget}
                        onClose={() => setEditTarget(null)}
                        onSaved={() => { setEditTarget(null); loadAll(); }}
                    />
                ) : null}
                {payTarget ? (
                    <CompletePaymentModal
                        booking={payTarget}
                        onClose={() => setPayTarget(null)}
                        onSaved={() => { setPayTarget(null); loadAll(); }}
                    />
                ) : null}
                {locView ? (
                    <LocationViewerDialog
                        open={!!locView}
                        onClose={() => setLocView(null)}
                        lat={locView.location_lat}
                        lng={locView.location_lng}
                        address={locView.location_text}
                    />
                ) : null}
            </div>
        </AppShell>
    );
}

// ── RECORDS (Months / Years — data-driven only) ──────────────────────
function CarBookingsRecords() {
    const navigate = useNavigate();
    const { company } = useAuth();
    const cur = getCurrency(company?.country);
    const [tab, setTab] = useState("months");           // months | years
    const [drill, setDrill] = useState(null);           // {kind:'month'|'year', key}
    const [summary, setSummary] = useState({ months: [], years: [] });
    const [items, setItems] = useState([]);

    const loadSummary = useCallback(async () => {
        try {
            const r = await api.get("/car-bookings/summary");
            setSummary(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { loadSummary(); }, [loadSummary]);
    useAutoRefresh(loadSummary, { intervalMs: 10000 });

    const loadItems = useCallback(async () => {
        if (!drill) { setItems([]); return; }
        try {
            const params = drill.kind === "month" ? { month: drill.key } : { year: drill.key };
            const r = await api.get("/car-bookings", { params });
            setItems(r.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    }, [drill]);
    useEffect(() => { loadItems(); }, [loadItems]);
    useAutoRefresh(loadItems, { intervalMs: 10000, enabled: !!drill });

    // Months tiles are STRICTLY driven by data (backend only returns months
    // that have at least one booking). No pre-created empty months.
    const monthsList = summary.months || [];
    const yearsList  = summary.years  || [];

    const drillItems = items;
    const totals = useMemo(() => drillItems.reduce((a, b) => ({
        amount: a.amount + (b.booking_amount || 0),
        paid:   a.paid   + (b.paid_amount    || 0),
        remain: a.remain + (b.remaining_amount || 0),
        count:  a.count  + 1,
    }), { amount: 0, paid: 0, remain: 0, count: 0 }), [drillItems]);

    // ── DRILL VIEW (specific month or year) ────────────────────────────
    if (drill) {
        const label = drill.kind === "month"
            ? `${MONTHS_AR[parseInt(drill.key.slice(5, 7), 10) - 1]} ${drill.key.slice(0, 4)}`
            : drill.key;
        return (
            <AppShell>
                <div className="mx-auto max-w-5xl">
                    <button
                        onClick={() => setDrill(null)}
                        className="mb-3 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
                        data-testid="cb-back-btn"
                    >
                        <ArrowRight className="h-4 w-4" /> العودة
                    </button>
                    <div className="mb-4">
                        <h1 className="font-heading text-2xl font-bold sm:text-3xl">{label}</h1>
                        <p className="mt-1 text-xs text-neutral-500">عرض الحجوزات المحفوظة لهذه الفترة</p>
                    </div>
                    {drillItems.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500">
                            لا توجد حجوزات لهذه الفترة
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
                            <table className="w-full text-right text-sm" data-testid="cb-drill-table">
                                <thead className="bg-neutral-50 text-[11px] font-bold uppercase text-neutral-500">
                                    <tr>
                                        <th className="px-3 py-2">التاريخ</th>
                                        <th className="px-3 py-2">الوقت</th>
                                        <th className="px-3 py-2">اسم العميل</th>
                                        <th className="px-3 py-2">الهاتف</th>
                                        <th className="px-3 py-2">النوع</th>
                                        <th className="px-3 py-2">ساعات</th>
                                        <th className="px-3 py-2">ساعات التدريب</th>
                                        <th className="px-3 py-2">المبلغ</th>
                                        <th className="px-3 py-2">مدفوع</th>
                                        <th className="px-3 py-2">متبقي</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drillItems.map((it) => (
                                        <tr key={it.id} className="border-t border-neutral-100 hover:bg-amber-50/40" data-testid={`cb-drill-row-${it.id}`}>
                                            <td className="px-3 py-2">
                                                <div className="font-mono text-xs">{it.date}</div>
                                                {it.date ? (
                                                    <div className="mt-0.5 text-[10px] font-bold text-neutral-500">
                                                        {weekdayAr(it.date)}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-2 font-mono text-xs text-neutral-700">
                                                {it.booking_time ? fmtTimeAr(it.booking_time) : "—"}
                                            </td>
                                            <td className="px-3 py-2 font-semibold">{it.customer_name}</td>
                                            <td className="px-3 py-2 font-mono text-xs">{it.phone}</td>
                                            <td className="px-3 py-2">{BOOKING_TYPE_LABEL_AR[it.booking_type] || it.booking_type}</td>
                                            <td className="px-3 py-2 font-numbers">{it.hours}</td>
                                            <td className="px-3 py-2">
                                                <TrainingHoursCell booking={it} onUpdated={loadItems} />
                                            </td>
                                            <td className="px-3 py-2 font-numbers font-bold">{it.booking_amount}</td>
                                            <td className="px-3 py-2 font-numbers text-emerald-700">{it.paid_amount}</td>
                                            <td className="px-3 py-2 font-numbers text-rose-700">{it.remaining_amount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="cb-drill-summary">
                        <StatCard label="عدد الحجوزات" value={totals.count} />
                        <StatCard label="إجمالي المبالغ" value={`${totals.amount} ${cur}`} tone="brand" />
                        <StatCard label="المدفوع" value={`${totals.paid} ${cur}`} tone="green" />
                        <StatCard label="المتبقي" value={`${totals.remain} ${cur}`} tone="red" />
                    </div>
                </div>
            </AppShell>
        );
    }

    // ── ROOT VIEW: Months / Years tabs ────────────────────────────────
    return (
        <AppShell>
            <div className="mx-auto max-w-5xl">
                <button
                    onClick={() => navigate("/car-bookings")}
                    className="mb-3 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
                    data-testid="cb-records-back-home"
                >
                    <ArrowRight className="h-4 w-4" /> دفتر العملاء
                </button>
                <div className="mb-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">Car Learn</p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl">السجلات</h1>
                    <p className="mt-1 text-xs text-neutral-500 sm:text-sm">العملاء المحفوظون مرتبين حسب الشهر والسنة</p>
                </div>
                <div className="mb-5 inline-flex rounded-2xl border border-neutral-200 bg-white p-1 shadow-sm" role="tablist">
                    <TabBtn active={tab === "months"} onClick={() => setTab("months")} testid="cb-tab-months">الأشهر</TabBtn>
                    <TabBtn active={tab === "years"}  onClick={() => setTab("years")}  testid="cb-tab-years">السنوات</TabBtn>
                </div>
                {tab === "months" ? (
                    monthsList.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500" data-testid="cb-months-empty">
                            لا توجد أشهر بعد — سيظهر الشهر تلقائياً عند إضافة أول عميل فيه
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {monthsList.map((m) => {
                                const [y, mo] = m.key.split("-");
                                return (
                                    <button
                                        key={m.key}
                                        onClick={() => setDrill({ kind: "month", key: m.key })}
                                        className="rounded-2xl border border-neutral-200 bg-white p-4 text-right shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                                        data-testid={`cb-month-${m.key}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-heading text-lg font-bold">{MONTHS_AR[parseInt(mo, 10) - 1]}</span>
                                            <span className="text-xs font-mono text-neutral-500">{y}</span>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between text-xs">
                                            <span className="text-neutral-500">{m.count} حجز</span>
                                            <span className="font-numbers font-bold text-amber-700">{m.total} {cur}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )
                ) : (
                    yearsList.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center text-neutral-500" data-testid="cb-years-empty">
                            لا توجد سجلات سنوية بعد
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2">
                            {yearsList.map((y) => (
                                <button
                                    key={y.key}
                                    onClick={() => setDrill({ kind: "year", key: y.key })}
                                    className="rounded-2xl border border-neutral-200 bg-white p-5 text-right shadow-sm transition hover:shadow-md"
                                    data-testid={`cb-year-${y.key}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading text-2xl font-bold">{y.key}</span>
                                        <ChevronLeft className="h-5 w-5 text-neutral-400" />
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                        <span className="text-neutral-500">{y.count} حجز</span>
                                        <span className="text-left font-numbers font-bold text-amber-700">{y.total} {cur}</span>
                                        <span className="text-emerald-700">مدفوع: {y.paid}</span>
                                        <span className="text-left text-rose-700">متبقي: {y.remaining}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )
                )}
            </div>
        </AppShell>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`rounded-xl px-6 py-2 text-sm font-bold transition ${active ? "bg-amber-700 text-white shadow" : "text-neutral-600 hover:bg-neutral-100"}`}
        >
            {children}
        </button>
    );
}

function StatCard({ label, value, tone = "neutral", testid }) {
    const tones = {
        brand:   "border-amber-200 bg-amber-50",
        green:   "border-emerald-200 bg-emerald-50",
        red:     "border-rose-200 bg-rose-50",
        violet:  "border-violet-200 bg-violet-50",
        neutral: "border-neutral-200 bg-white",
    };
    const textTones = {
        brand:   "text-amber-800",
        green:   "text-emerald-800",
        red:     "text-rose-800",
        violet:  "text-violet-800",
        neutral: "text-neutral-800",
    };
    return (
        <div className={`rounded-2xl border p-3 text-center ${tones[tone]}`} data-testid={testid}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{label}</div>
            <div className={`mt-1 font-numbers text-xl font-extrabold ${textTones[tone]}`}>{value}</div>
        </div>
    );
}

function BalanceInputCard({ value, onChange }) {
    return (
        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 text-center" data-testid="cb-balance-card">
            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">الرصيد</div>
            <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.]?[0-9]*"
                value={value}
                onChange={onChange}
                placeholder="0"
                aria-label="الرصيد"
                data-testid="cb-balance-input"
                className="mt-1 w-full bg-transparent text-center font-numbers text-xl font-extrabold text-sky-900 outline-none placeholder:text-sky-300 focus:outline-none"
            />
        </div>
    );
}

function AddBookingModal({ onClose, onSaved, booking = null }) {
    const { company } = useAuth();
    const cur = getCurrency(company?.country);
    const isEdit = !!booking;
    const [customerName, setCustomerName] = useState(booking?.customer_name || "");
    const [phone, setPhone] = useState(booking?.phone || "");
    const [locationText, setLocationText] = useState(booking?.location_text || "");
    const [locationLat, setLocationLat] = useState(booking?.location_lat ?? null);
    const [locationLng, setLocationLng] = useState(booking?.location_lng ?? null);
    const [showLocPicker, setShowLocPicker] = useState(false);
    const [bookingType, setBookingType] = useState(booking?.booking_type || "full");
    const [hours, setHours] = useState(booking?.hours ? String(booking.hours) : "");
    const [paid, setPaid] = useState(booking?.paid_amount != null ? String(booking.paid_amount) : "");
    const [date, setDate] = useState(booking?.date || new Date().toISOString().slice(0, 10));
    const [bookingTime, setBookingTime] = useState(() => booking?.booking_time ? parse24h(booking.booking_time) : null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    // Debounced server-side price preview (source of truth)
    useEffect(() => {
        const h = parseInt(hours, 10);
        if (!h || h <= 0) { setPreview(null); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/car-bookings/price-preview", { params: { booking_type: bookingType, hours: h } });
                setPreview(r.data);
            } catch { setPreview(null); }
        }, 200);
        return () => clearTimeout(t);
    }, [bookingType, hours]);

    const remaining = useMemo(() => {
        if (!preview) return null;
        return Math.max(0, preview.total_amount - (parseFloat(paid) || 0));
    }, [preview, paid]);

    const onSave = async () => {
        setBusy(true);
        try {
            const body = {
                customer_name: customerName, phone,
                booking_type: bookingType,
                hours: parseInt(hours, 10),
                paid_amount: parseFloat(paid) || 0,
                date,
                booking_time: bookingTime ? build24h(bookingTime) : null,
                location_text: locationText || null,
                location_lat: locationLat,
                location_lng: locationLng,
            };
            if (isEdit) {
                await api.put(`/car-bookings/${booking.id}`, body);
                toast.success("تم حفظ التعديلات");
            } else {
                await api.post("/car-bookings", body);
                toast.success("تم حفظ الحجز");
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-4 pb-4 pt-10 sm:items-center" onClick={onClose} data-testid="cb-add-modal">
            <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()} dir="rtl">
                <div className="border-b border-neutral-200 px-5 py-4">
                    <div className="font-heading text-lg font-bold">{isEdit ? "تعديل بيانات العميل" : "عميل جديد"}</div>
                </div>
                <div className="space-y-3 px-5 py-4">
                    <Field label="التاريخ">
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            data-testid="cb-input-date"
                        />
                        {date ? (
                            <p className="mt-1 text-[11px] font-bold text-neutral-500" data-testid="cb-input-weekday">
                                {weekdayAr(date)}
                            </p>
                        ) : null}
                    </Field>
                    <Field label="وقت الحجز">
                        <TimePicker value={bookingTime} onChange={setBookingTime} />
                    </Field>
                    <Field label="اسم العميل"><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="cb-input-name" /></Field>
                    <Field label="الموقع">
                        <div className="space-y-2">
                            <Input
                                value={locationText}
                                onChange={(e) => setLocationText(e.target.value)}
                                placeholder="مثال: حي الياسمين - الرياض"
                                data-testid="cb-input-location-text"
                            />
                            <div className="flex items-center justify-between gap-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setShowLocPicker(true)}
                                    className="gap-1.5"
                                    data-testid="cb-open-map-btn"
                                >
                                    <MapPin className="h-4 w-4 text-amber-700" />
                                    تحديد على الخريطة
                                </Button>
                                {locationLat != null && locationLng != null ? (
                                    <div className="flex items-center gap-2 text-[11px]">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-800 ring-1 ring-emerald-200" data-testid="cb-location-saved-chip">
                                            <CheckCircle2 className="h-3 w-3" />
                                            موقع محفوظ
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => { setLocationLat(null); setLocationLng(null); }}
                                            className="text-rose-600 hover:underline"
                                            data-testid="cb-clear-pin-btn"
                                        >
                                            إزالة
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </Field>
                    <Field label="رقم الهاتف"><Input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" data-testid="cb-input-phone" /></Field>
                    <Field label="نوع الحجز">
                        <Select value={bookingType} onValueChange={setBookingType}>
                            <SelectTrigger data-testid="cb-input-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="full">حجز كامل (125 {cur}/ساعة)</SelectItem>
                                <SelectItem value="full_no_delivery">حجز كامل بدون توصيل (100 {cur}/ساعة)</SelectItem>
                                <SelectItem value="full_with_trainee_car">حجز كامل مع توصيل بسيارة المتدرب (100 {cur}/ساعة)</SelectItem>
                                <SelectItem value="trainee_car_no_delivery">حجز بسيارة المتدربة بدون توصيل (80 {cur}/ساعة)</SelectItem>
                                <SelectItem value="partial">حجز جزئي (150/145/135 حسب الساعات)</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="عدد الساعات"><Input type="number" min="1" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="cb-input-hours" /></Field>
                    {preview ? (
                        <div className="rounded-xl bg-amber-50 p-3 text-sm ring-1 ring-amber-200" data-testid="cb-preview">
                            <div>سعر الساعة: <b className="font-numbers">{preview.hourly_rate} {cur}</b></div>
                            <div>الإجمالي: <b className="font-numbers text-amber-900">{preview.total_amount} {cur}</b></div>
                            {preview.auto_promoted ? <div className="mt-1 text-[11px] font-bold text-amber-700">⚡ تحول تلقائياً إلى حجز كامل (10 ساعات فأكثر)</div> : null}
                        </div>
                    ) : null}
                    <Field label={`المبلغ المدفوع (${cur})`}><Input type="number" min="0" value={paid} onChange={(e) => setPaid(e.target.value)} data-testid="cb-input-paid" /></Field>
                    {remaining !== null ? <div className="text-sm">المتبقي: <b className="font-numbers text-rose-700">{remaining} {cur}</b></div> : null}
                </div>
                <div className="flex gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-3">
                    <Button variant="outline" onClick={onClose} className="flex-1">إلغاء</Button>
                    <Button onClick={onSave} disabled={busy || !customerName || !phone || !hours || !preview} className="flex-1 gap-2 bg-amber-700 hover:bg-amber-800" data-testid="cb-save-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarIcon className="h-4 w-4" />}
                        حفظ
                    </Button>
                </div>
            </div>
            <LocationPickerDialog
                open={showLocPicker}
                onClose={() => setShowLocPicker(false)}
                initialLat={locationLat}
                initialLng={locationLng}
                initialText={locationText}
                onConfirm={({ lat, lng, address }) => {
                    setLocationLat(lat);
                    setLocationLng(lng);
                    if (address && !locationText.trim()) setLocationText(address);
                    setShowLocPicker(false);
                }}
            />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="mb-1 block text-xs font-bold text-neutral-700">{label}</Label>
            {children}
        </div>
    );
}

/** Inline-editable training hours input. Colours green when it matches the
 *  booked hours (training complete) and red otherwise. Debounced auto-save
 *  after 500 ms of inactivity and on blur. */
function TrainingHoursCell({ booking, onUpdated }) {
    const booked = Number(booking.hours || 0);
    const initial = booking.training_hours == null ? 0 : Number(booking.training_hours);
    const [val, setVal] = useState(String(initial));
    // Keep local state in sync with the row when it refreshes.
    useEffect(() => { setVal(String(initial)); }, [booking.id, initial]);

    const parsed = parseInt(val, 10);
    const num = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    const complete = num === booked && booked > 0;
    const tone = complete
        ? "bg-emerald-50 text-emerald-800 ring-emerald-300 focus:ring-emerald-500"
        : "bg-rose-50 text-rose-800 ring-rose-300 focus:ring-rose-500";

    const save = async (v) => {
        try {
            await api.post(`/car-bookings/${booking.id}/training-hours`, { training_hours: v });
            onUpdated?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Debounced save on every keystroke.
    useEffect(() => {
        if (num === initial) return;
        const t = setTimeout(() => save(num), 500);
        return () => clearTimeout(t);
    }, [num, initial]);

    const onChange = (e) => {
        // Digits only.
        const cleaned = (e.target.value || "").replace(/[^\d]/g, "");
        setVal(cleaned);
    };

    return (
        <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            value={val}
            onChange={onChange}
            onBlur={() => num !== initial && save(num)}
            aria-label="ساعات التدريب"
            data-testid={`cb-training-${booking.id}`}
            className={`w-16 rounded-lg px-2 py-1 text-center font-numbers text-sm font-bold outline-none ring-1 transition ${tone}`}
        />
    );
}

/** Compact circular brand logo used in the page header. Falls back to a
 *  dashed circular placeholder if the image is missing. */
function BrandLogo({ src, alt }) {
    const [failed, setFailed] = useState(false);
    if (failed || !src) {
        return (
            <div
                className="flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-dashed border-neutral-300 bg-neutral-50 text-center sm:h-32 sm:w-32"
                data-testid="cb-brand-placeholder"
            >
                <ImageIcon className="h-6 w-6 text-neutral-400" />
                <p className="mt-1 text-[9px] font-bold text-neutral-500">الشعار</p>
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={alt || ""}
            onError={() => setFailed(true)}
            className="h-24 w-24 rounded-full object-cover shadow-md ring-2 ring-amber-200 sm:h-32 sm:w-32"
            data-testid="cb-brand-image"
        />
    );
}

/** Arabic 12-hour time picker with a nullable state — the field starts empty
 *  (nothing selected) and the user must tap "تعيين الوقت" to activate the
 *  three dropdowns. A small "×" clears the value back to null. This matches
 *  the requirement that legacy customers keep an empty booking_time until the
 *  user explicitly sets one. `value` is `null` or `{hour12, minute, period}`. */
function TimePicker({ value, onChange }) {
    const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
    const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

    if (!value) {
        return (
            <div className="flex items-center gap-2" data-testid="cb-input-time-empty">
                <button
                    type="button"
                    onClick={() => onChange({ hour12: "8", minute: "00", period: "am" })}
                    className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 text-xs font-bold text-neutral-600 transition hover:border-amber-400 hover:bg-amber-50 hover:text-amber-800"
                    data-testid="cb-input-time-set"
                >
                    + تعيين الوقت
                </button>
                <span className="text-[11px] text-neutral-400">(اختياري)</span>
            </div>
        );
    }
    const { hour12, minute, period } = value;
    const set = (patch) => onChange({ hour12, minute, period, ...patch });
    return (
        <div className="grid grid-cols-[repeat(3,minmax(0,1fr))_auto] items-center gap-2" data-testid="cb-input-time">
            <Select value={hour12} onValueChange={(v) => set({ hour12: v })}>
                <SelectTrigger data-testid="cb-input-time-hour"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {hours.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={minute} onValueChange={(v) => set({ minute: v })}>
                <SelectTrigger data-testid="cb-input-time-minute"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {minutes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={period} onValueChange={(v) => set({ period: v })}>
                <SelectTrigger data-testid="cb-input-time-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="am">صباحاً</SelectItem>
                    <SelectItem value="pm">مساءً</SelectItem>
                </SelectContent>
            </Select>
            <button
                type="button"
                onClick={() => onChange(null)}
                className="rounded-lg p-1.5 text-neutral-400 hover:bg-rose-50 hover:text-rose-600"
                aria-label="مسح الوقت"
                data-testid="cb-input-time-clear"
            >
                ✕
            </button>
        </div>
    );
}

function CompletePaymentModal({ booking, onClose, onSaved }) {
    const { company } = useAuth();
    const cur = getCurrency(company?.country);
    const [amountStr, setAmountStr] = useState("");
    const [busy, setBusy] = useState(false);
    const currentPaid = Number(booking.paid_amount || 0);
    const currentRemaining = Number(booking.remaining_amount || 0);
    const total = Number(booking.booking_amount || 0);

    // Numeric-only sanitiser: allow digits + a single decimal point, nothing else.
    const onChange = (e) => {
        const raw = e.target.value ?? "";
        let cleaned = raw.replace(/[^\d.]/g, "");
        const firstDot = cleaned.indexOf(".");
        if (firstDot !== -1) {
            cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
        }
        setAmountStr(cleaned);
    };

    const amount = parseFloat(amountStr);
    const isValid = Number.isFinite(amount) && amount > 0 && amount <= currentRemaining + 1e-9;
    const newPaid = isValid ? currentPaid + amount : currentPaid;
    const newRemaining = isValid ? Math.max(0, total - newPaid) : currentRemaining;
    const willComplete = isValid && newRemaining <= 1e-9;

    const onSave = async () => {
        if (!isValid) return;
        setBusy(true);
        try {
            await api.post(`/car-bookings/${booking.id}/pay`, { amount });
            toast.success(willComplete ? "تم السداد بالكامل" : "تم تحديث المبلغ المدفوع");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-4 pb-4 pt-10 sm:items-center"
            onClick={onClose}
            data-testid="cb-pay-modal"
        >
            <div
                className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
            >
                <div className="border-b border-neutral-200 px-5 py-4">
                    <div className="font-heading text-lg font-bold">إكمال المبلغ</div>
                    <div className="mt-0.5 text-xs text-neutral-500">{booking.customer_name} · {booking.phone}</div>
                </div>
                <div className="space-y-3 px-5 py-4">
                    <div className="grid grid-cols-3 gap-2 rounded-xl bg-neutral-50 p-3 text-xs">
                        <div>
                            <div className="text-neutral-500">مبلغ الحجز</div>
                            <div className="font-numbers font-bold text-neutral-800">{total} {cur}</div>
                        </div>
                        <div>
                            <div className="text-neutral-500">المدفوع حالياً</div>
                            <div className="font-numbers font-bold text-emerald-700">{currentPaid} {cur}</div>
                        </div>
                        <div>
                            <div className="text-neutral-500">المتبقي حالياً</div>
                            <div className="font-numbers font-bold text-rose-700">{currentRemaining} {cur}</div>
                        </div>
                    </div>

                    <Field label={`المبلغ المضاف (${cur})`}>
                        <Input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*[.]?[0-9]*"
                            value={amountStr}
                            onChange={onChange}
                            placeholder="0"
                            autoFocus
                            data-testid="cb-pay-input"
                        />
                        <p className="mt-1 text-[11px] text-neutral-500">أرقام فقط · لا يتجاوز {currentRemaining} {cur}</p>
                    </Field>

                    {isValid ? (
                        <div className="rounded-xl bg-emerald-50 p-3 text-sm ring-1 ring-emerald-200" data-testid="cb-pay-preview">
                            <div className="flex items-center justify-between">
                                <span className="text-neutral-600">المدفوع الجديد</span>
                                <b className="font-numbers text-emerald-800">{newPaid} {cur}</b>
                            </div>
                            <div className="mt-1 flex items-center justify-between">
                                <span className="text-neutral-600">المتبقي الجديد</span>
                                <b className={`font-numbers ${willComplete ? "text-emerald-800" : "text-rose-700"}`}>
                                    {newRemaining} {cur}
                                </b>
                            </div>
                            {willComplete ? (
                                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
                                    <CheckCircle2 className="h-3 w-3" /> سيصبح مكتمل السداد
                                </div>
                            ) : null}
                        </div>
                    ) : amountStr ? (
                        <div className="rounded-xl bg-rose-50 p-2 text-xs text-rose-700 ring-1 ring-rose-200">
                            المبلغ غير صالح — يجب أن يكون أكبر من صفر ولا يتجاوز المتبقي
                        </div>
                    ) : null}
                </div>
                <div className="flex gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-3">
                    <Button variant="outline" onClick={onClose} className="flex-1" data-testid="cb-pay-cancel">إلغاء</Button>
                    <Button
                        onClick={onSave}
                        disabled={busy || !isValid}
                        className="flex-1 gap-2 bg-emerald-700 hover:bg-emerald-800"
                        data-testid="cb-pay-save"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleDollarSign className="h-4 w-4" />}
                        حفظ
                    </Button>
                </div>
            </div>
        </div>
    );
}
